/* Outcome + score composition.
 *
 * Outcome is binary (closed / not_closed) per CODEBABY_GUIDE.md §10
 * and is driven by the presence of forward-commitment milestones,
 * NOT by the score. Score is independent quality of session.
 *
 * The "soft yes" hero state — closed with growths >= wins — is
 * derived by the debrief composer from the verdict, not stored as a
 * separate outcome. */

import type { PersonaId } from '@/lib/codebaby';
import { defaultMilestoneWeight } from './milestones';
// COMMIT_MILESTONES (the per-persona "forward commitment" tags) is vertical
// content — it lives in the grading pack alongside the milestone vocabulary.
import { COMMIT_MILESTONES } from '@/seed/milestones';
import type { MilestoneEvent, MilestoneTag, SessionOutcome, Verdict } from './types';

function clampScore(score: number): number {
  if (Number.isNaN(score)) return 0;
  if (score < 0) return 0;
  if (score > 100) return 100;
  return Math.round(score);
}

export interface ComposeVerdictArgs {
  personaId: PersonaId;
  milestones: MilestoneEvent[];
  /** Last 0..4 level from the temperature history. */
  finalTemperatureLevel: number;
  /** From persona / scenario content. */
  expectedMilestones: MilestoneTag[];
  /** Hard-stops the session as not-closed (e.g., persona walked out). */
  forceNotClosed?: boolean;
}

export function composeVerdict(args: ComposeVerdictArgs): Verdict {
  const { personaId, milestones, finalTemperatureLevel, expectedMilestones, forceNotClosed } = args;

  const milestonesHit: MilestoneTag[] = Array.from(new Set(milestones.map((m) => m.tag)));
  const commitTags = COMMIT_MILESTONES[personaId] ?? [];
  // "Closed" requires the producer to have secured the persona's commitment.
  // For these personas the commit milestones ARE the full set of moves that
  // earn it (they match the scenario's expected list), so a real close means
  // ALL of them landed — not just one. Hitting a single commit-tagged move used
  // to flip a half-finished session to "DEAL CLOSED", contradicting both the
  // "X of N" milestone count and the live gauge (the reported Dan bug: 1/3
  // milestones, lukewarm, yet "closed"). Personas that start warm (Rita opens at
  // 4/4) made a temperature gate useless, so we gate on the milestones instead.
  const hitCommit =
    !forceNotClosed &&
    commitTags.length > 0 &&
    commitTags.every((tag) => milestonesHit.includes(tag));
  const walkedOut = milestonesHit.includes('account_closed');

  const outcome: SessionOutcome = !walkedOut && hitCommit ? 'closed' : 'not_closed';

  // Score: start at 50 (a "neutral, completed" session), add signed
  // milestone contributions, lightly bias by final temperature.
  let raw = 50;
  for (const tag of milestonesHit) {
    raw += defaultMilestoneWeight(tag);
  }
  // Temperature bias: at level 4, +6; at level 0, -6.
  raw += (finalTemperatureLevel - 2) * 3;

  const missedMilestones = expectedMilestones.filter((tag) => !milestonesHit.includes(tag));
  // Mild penalty for each expected-but-missed (separate from explicit growths).
  raw -= missedMilestones.length * 3;

  const winsCount = milestones.filter((m) => m.momentType === 'win').length;
  const growthsCount = milestones.filter((m) => m.momentType === 'growth').length;

  return {
    outcome,
    score: clampScore(raw),
    finalTemperatureLevel,
    milestonesHit,
    missedMilestones,
    winsCount,
    growthsCount,
  };
}
